import React, { useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag, Tag, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { fmt } from "../../mock";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PayPalButton } from "./PayPalButton";

const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, total, clear } = useCart();
  const [step, setStep] = useState("cart");
  const { user } = useAuth();
  const empty = { name: "", email: "", phone: "", address: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [couponIn, setCouponIn] = useState("");
  const [coupon, setCoupon] = useState(null);
  const discount = coupon ? Math.min(coupon.type === "percent" ? total * coupon.value / 100 : coupon.value, total) : 0;
  const [pays, setPays] = useState([]);
  const [pm, setPm] = useState(null);
  const fee = pm === "contrassegno" ? Number(pays.find((x) => x.id === pm)?.fee || 0) : 0;
  const final = total - discount + fee;

  const applyCoupon = async () => {
    try { const { data } = await api.post("/coupons/validate", { code: couponIn, subtotal: total, items: items.map((i) => ({ product_id: i.id, qty: i.qty })) }); setCoupon(data); toast.success(`Codice ${data.code} applicato`); }
    catch (err) { setCoupon(null); toast.error(errMsg(err, "Codice non valido")); }
  };

  const goCheckout = () => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
    setStep("checkout");
    api.get("/payment-methods").then(({ data }) => { setPays(data); setPm((v) => v || data[0]?.id || null); }).catch(() => {});
  };

  const checkout = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/orders", { customer: form, items: items.map((i) => ({ product_id: i.id, qty: i.qty })), coupon_code: coupon?.code || null, payment_method: pm });
      if (pm === "carta") {
        const { data: s } = await api.post("/payments/checkout", { order_id: data.order_id, origin_url: window.location.origin });
        clear();
        window.location.href = s.checkout_url;
        return;
      }
      if (pm === "paypal") {
        setDone(data); clear(); setForm(empty); setCoupon(null); setCouponIn(""); setStep("paypal");
        return;
      }
      setDone(data);
      clear(); setForm(empty); setCoupon(null); setCouponIn(""); setStep("done");
    } catch (err) {
      toast.error(errMsg(err, "Invio ordine non riuscito"));
    } finally { setSending(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setStep("cart"); setDone(null); } }}>
      <SheetContent side="right" className="w-full sm:max-w-[420px] bg-white flex flex-col" data-testid="cart-drawer">
        <SheetTitle className="text-[22px] font-bold text-[#1d1e20]">{step === "checkout" ? "Checkout" : step === "done" ? "Ordine inviato" : step === "paypal" ? "Paga con PayPal" : "Carrello"}</SheetTitle>
        {!user && step !== "done" ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-3" data-testid="cart-login-required">
            <Lock className="w-12 h-12 text-[#b8c0cc]" strokeWidth={1.3} />
            {items.length > 0 && <p className="text-[14px] font-bold text-[#1d1e20]" data-testid="cart-items-pending">{items.reduce((s, i) => s + i.qty, 0)} articol{items.reduce((s, i) => s + i.qty, 0) === 1 ? "o" : "i"} nel carrello: {items.map((i) => i.name).join(", ")}</p>}
            <p className="text-[#56585e]">Accedi all'Area Clienti per vedere prezzi e completare l'ordine</p>
            <Link to="/area-clienti" onClick={() => setOpen(false)} className="es-btn-primary h-[40px] px-6 text-[14px]" data-testid="cart-login-btn">Accedi o registrati</Link>
          </div>
        ) : step === "paypal" && done ? (
          <div className="flex-1 flex flex-col mt-4 gap-4" data-testid="paypal-step">
            <div className="rounded-2xl border border-[#e0e0e0] p-4 bg-[#f7f7f8]">
              <p className="text-[13px] text-[#56585e]">Ordine <strong className="text-[#1d1e20]" data-testid="paypal-order-code">{done.order_id}</strong> creato: completa il pagamento qui sotto per confermarlo.</p>
              <p className="text-[13px] text-[#56585e] mt-1">{done.items.map((i) => `${i.name} x${i.qty}`).join(", ")}</p>
              <p className="text-[20px] font-bold text-[#CF1072] mt-2" data-testid="paypal-order-total">Totale {fmt(done.total)}</p>
            </div>
            <PayPalButton kind="order" refId={done.order_id} onPaid={() => setDone((d) => ({ ...d, paid: true, email_sent: true }))} />
            {done.paid && <button className="es-btn-primary h-[46px] text-[15px]" onClick={() => { setOpen(false); setStep("cart"); setDone(null); }} data-testid="paypal-done-close">Grazie! Chiudi</button>}
            {!done.paid && <p className="text-[12px] text-[#9aa0a6] text-center mt-auto">Se chiudi ora, trovi l'ordine in attesa di pagamento nella tua Area Clienti → Ordini.</p>}
          </div>
        ) : step === "done" && done ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-3" data-testid="order-success">
            <div className="w-16 h-16 rounded-full bg-[#CF1072]/10 flex items-center justify-center"><ShoppingBag className="w-8 h-8 text-[#CF1072]" /></div>
            <p className="text-[18px] font-bold text-[#1d1e20]">Grazie, {done.customer.name}!</p>
            <p className="text-[14px] text-[#56585e]">Ordine <strong data-testid="order-code">{done.order_id}</strong> ricevuto. {done.email_sent ? "Ti abbiamo inviato una email di conferma." : "Ti contatteremo a breve per la conferma."}</p>
            <p className="text-[16px] font-bold text-[#CF1072]">Totale {fmt(done.total)}</p>
            <button className="es-btn-primary h-[40px] px-6 text-[14px] mt-2" onClick={() => { setOpen(false); setStep("cart"); setDone(null); }}>Continua</button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-3" data-testid="cart-empty">
            <ShoppingBag className="w-12 h-12 text-[#b8c0cc]" strokeWidth={1.3} />
            <p className="text-[#56585e]">Il tuo carrello è vuoto</p>
            <Link to="/negozio" className="es-btn-primary h-[40px] px-6 text-[14px]" onClick={() => setOpen(false)} data-testid="cart-go-shop">Vai al negozio</Link>
          </div>
        ) : step === "cart" ? (
          <>
            <div className="flex-1 overflow-y-auto mt-4 space-y-4 pr-1">
              {items.map((i) => (
                <div key={i.id} className="flex gap-3 border-b border-[#e0e0e0] pb-4" data-testid={`cart-item-${i.id}`}>
                  <img src={i.img} alt={i.name} className="w-20 h-14 object-cover rounded" />
                  <div className="flex-1">
                    <p className="text-[14px] font-bold text-[#1d1e20] leading-tight">{i.name}</p>
                    <p className="text-[13px] text-[#CF1072] mt-1">{fmt(i.price)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button className="p-1 border rounded-full hover:border-[#CF1072]" onClick={() => setQty(i.id, i.qty - 1)}><Minus className="w-3 h-3" /></button>
                      <span className="text-[13px] w-5 text-center">{i.qty}</span>
                      <button className="p-1 border rounded-full hover:border-[#CF1072]" onClick={() => setQty(i.id, i.qty + 1)}><Plus className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <button onClick={() => remove(i.id)} className="text-[#56585e] hover:text-[#CF1072] self-start" data-testid={`cart-remove-${i.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="border-t border-[#e0e0e0] pt-4">
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1"><Tag className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#56585e]" />
                  <input value={couponIn} onChange={(e) => setCouponIn(e.target.value.toUpperCase())} placeholder="Codice sconto" className="es-input h-[40px] uppercase" style={{ paddingLeft: 36 }} data-testid="coupon-input" /></div>
                {coupon ? <button onClick={() => { setCoupon(null); setCouponIn(""); }} className="h-[40px] px-4 rounded-full border text-[13px] text-[#56585e]" data-testid="coupon-remove">Rimuovi</button>
                  : <button onClick={applyCoupon} disabled={!couponIn.trim()} className="h-[40px] px-4 rounded-full border border-[#CF1072] text-[#CF1072] text-[13px] disabled:opacity-40 hover:bg-[#CF1072]/5" data-testid="coupon-apply">Applica</button>}
              </div>
              {coupon && <div className="flex justify-between text-[14px] text-[#56585e]"><span>Subtotale</span><span>{fmt(total)}</span></div>}
              {coupon && <div className="flex justify-between text-[14px] text-[#CF1072] mb-1" data-testid="coupon-discount"><span>Sconto {coupon.code}</span><span>-{fmt(discount)}</span></div>}
              <div className="flex justify-between text-[16px] font-bold text-[#1d1e20]"><span>Totale</span><span data-testid="cart-total">{fmt(final)}</span></div>
              <button className="es-btn-primary w-full h-[46px] mt-4 text-[15px]" onClick={goCheckout} data-testid="checkout-button">Procedi all'ordine</button>
            </div>
          </>
        ) : (
          <form onSubmit={checkout} className="flex-1 flex flex-col gap-4 mt-4" data-testid="checkout-form">
            {[
              ["name", "Nome e cognome", "text"],
              ["email", "Email", "email"],
              ["phone", "Telefono", "tel"],
              ["address", "Indirizzo di consegna (opzionale)", "text"],
              ["notes", "Note (opzionale)", "text"],
            ].map(([k, l, t]) => (
              <label key={k} className="text-[13px] text-[#1d1e20]">
                {l}
                <input required={!["address", "notes"].includes(k)} type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="es-input mt-1" data-testid={`checkout-${k}`} />
              </label>
            ))}
            <div data-testid="payment-methods">
              <p className="text-[13px] text-[#1d1e20] mb-2">Modalità di pagamento</p>
              <div className="space-y-2">
                {pays.map((x) => (
                  <label key={x.id} className={`flex gap-3 items-start border rounded-xl p-3 cursor-pointer transition-colors duration-200 ${pm === x.id ? "border-[#CF1072] bg-[#CF1072]/5" : "border-[#e0e0e0]"}`} data-testid={`pay-option-${x.id}`}>
                    <input type="radio" name="pm" checked={pm === x.id} onChange={() => setPm(x.id)} className="mt-1 accent-[#CF1072]" />
                    <span className="text-[13px]"><strong>{x.label}</strong>{x.id === "contrassegno" && x.fee ? ` (+${fmt(x.fee)})` : ""}
                      {pm === x.id && <span className="block text-[12px] text-[#56585e] mt-0.5">{[x.iban && `IBAN ${x.iban}`, x.holder && `intestato a ${x.holder}`, x.address, x.note].filter(Boolean).join(" - ")}</span>}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-between text-[16px] font-bold mt-auto"><span>Totale{coupon ? ` (sconto ${coupon.code})` : ""}</span><span data-testid="checkout-total">{fmt(final)}</span></div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setStep("cart")} className="flex-1 h-[46px] rounded-full border border-[#CF1072] text-[#CF1072] hover:bg-[#CF1072]/5">Indietro</button>
              <button type="submit" disabled={sending} className="es-btn-primary flex-1 h-[46px] disabled:opacity-60" data-testid="place-order">{sending ? "Invio..." : pm === "carta" ? "Paga con carta" : pm === "paypal" ? "Continua con PayPal" : "Invia ordine"}</button>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
